import type { Logger } from '@feature-hub/core';
import type { HistoryServiceV2 } from '@feature-hub/history-service';
import type { MocksServerName } from '@visualizer/bff';
import type { ApiKeyProviderV1 } from '@volkswagen-onehub/api-key-provider';
import { DataVersionServiceV1 } from '@volkswagen-onehub/data-version-service';
import type { MocksServerConfig } from '@volkswagen-onehub/fetch-json';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { ServiceConfigEndpointProvider } from '@volkswagen-onehub/service-config-endpoint-provider';

import type { BffFetchOptions, RegisterRequestMetaCB } from '../bff-utils/fetch-bff-data';

interface Config {
	readonly mockIds?: string;
	readonly mocksServerConfig?: MocksServerConfig;
	readonly enableTracing?: boolean;
}

interface FeatureServices {
	readonly 'api-key-provider'?: ApiKeyProviderV1;
	readonly 'data-version-service': DataVersionServiceV1;
	readonly 'locale-service': LocaleServiceV1;
	readonly 's2:history': HistoryServiceV2;
	readonly 's2:logger'?: Logger;
	readonly 'service-config-endpoint-provider': ServiceConfigEndpointProvider;
}

export function createBffFetchOptions({
	baseUrl,
	config,
	featureServices,
	registerRequestMeta
}: {
	baseUrl: string | undefined;
	config: Config;
	featureServices: FeatureServices;
	registerRequestMeta?: RegisterRequestMetaCB;
}): BffFetchOptions {
	const {
		'api-key-provider': apiKeyProvider,
		'data-version-service': dataVersionService,
		'locale-service': localeService,
		's2:history': historyService,
		's2:logger': logger = console,
		'service-config-endpoint-provider': serviceConfigEndpointProvider
	} = featureServices;

	const rootSearchParams = new URLSearchParams(historyService.rootHistory.location.search);

	const mocksServerSettings = config.mocksServerConfig && getMocksServerSettings(config.mocksServerConfig);

	const dataVersion = `${dataVersionService.serviceConfigsVersion}`;

	const cacheBuster = 'modelCacheBuster' in dataVersionService ? dataVersionService.modelCacheBuster : dataVersion;

	return {
		baseUrl,
		logger,
		language: localeService.language,
		countryCode: localeService.countryCode,
		serviceConfigEndpointToken: serviceConfigEndpointProvider.endpoint,
		cacheBustingAllowed: serviceConfigEndpointProvider.cacheBustingAllowed,
		dataVersion,
		cacheBuster,
		mockIds: config.mockIds?.split('-'),
		...mocksServerSettings,
		// In the CMS the `apiKey` can also be `null`, which is not included in the
		// types of the Api Key Provider.
		oneapiKey: apiKeyProvider?.apiKey || undefined,
		// Never apply the 'debug' parameter during SSR
		debug: typeof window === 'undefined' ? undefined : rootSearchParams.get('debug') || undefined,
		registerRequestMeta
	};
}

function getMocksServerSettings(config: MocksServerConfig): {
	mocksServerName: MocksServerName | undefined;
	mocksServerPort: string | undefined;
	mocksServerCached: boolean;
} {
	const mocksServerName = config.baseUrl.startsWith('https://mocks.onehub.feature-app.io')
		? 'global'
		: config.baseUrl.startsWith('http://localhost')
			? 'local'
			: undefined;

	const mocksServerPort =
		mocksServerName === 'local' ? config.baseUrl.split('http://localhost:')[1]?.split('/')[0] : undefined;

	const mocksServerCached = config.baseUrl.endsWith('/cached') || false;

	return { mocksServerName, mocksServerPort, mocksServerCached };
}
